import React, { useEffect, useState, useCallback } from "react";
import { Flex, Select, Skeleton } from "antd";
import { useParams } from "react-router-dom";
import { IAuditLogResponse, IAuditLogResult } from "../../interfaces/audit-log";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { AuditLogServices } from "../../services/audit-log";
import { ISortInfo } from "../../interfaces/common";
import { formatDateDDMMYYYY, formatDateDDMMYYYY_HHMMSS, formatEuropeanCurrencyIntl, formatNumberFixed } from "../../utils/helper";
import BackButton from "../../components/back-button";
import { AuditItem } from "../../components/audit-trail/audit-trail";
import InfiniteAuditTrail from "../../components/audit-trail/infinite-audit-trail";


const fieldLabels: Record<string, string> = {
  // === GENERAL_INFO ===
  supplierCode: "Supplier Code",
  supplierName: "Supplier Name",
  alternativeName: "Alternative Name",
  alternativeNames: "Alternative Names",
  supplierAddress: "Supplier Address",
  country: "Country",
  contactName: "Contact Name",
  phoneNumber: "Phone Number",
  contactEmail1: "Contact Email 1",
  contactEmail2: "Contact Email 2",
  contactEmail3: "Invoice sender email",
  isCreditSupplier: "Is Credit Supplier",
  isCreditLimit: "Is Credit Limit",
  creditLimitNote: "Credit Limit Note",
  creditLimitValue: "Credit Limit Value",
  paymentMethod: "Payment Method",
  isRfi21FormRequired: "Is RFI21 Form Required",
  isPartOfAGroup: "Is Part of a Group",
  groupName: "Group Name",
  productHead: "Product Head",
  productOwner: "Product Owner",
  apOwner: "AP Owner",
  apEscalation: "AP Escalation",
  isActive: "Is Active",

  // === TERMS ===
  termType: "Term Type",
  termName: "Term Name",
  validFrom: "Valid From",
  validTo: "Valid To",
  noOfInstallments: "Number of Installments",

  // === Installment (from buildTermsFields) ===
  // Deposit
  depositTiming: "[Deposit] Timing",
  depositEventType: "[Deposit] Event Type",
  depositDuration: "[Deposit] Duration",
  depositCalculationBasis: "[Deposit] Calculation Basis",
  depositPercentageAmount: "De[Deposit]posit Percentage Amount",
  depositAbsoluteAmount: "[Deposit] Absolute Amount",
  depositAbsoluteDriver: "[Deposit] Absolute Driver",
  depositAmount: "[Deposit] Amount",

  // Stage 1
  stage1Timing: "[Stage 1] Timing",
  stage1EventType: "[Stage 1] Event Type",
  stage1Duration: "[Stage 1] Duration",
  stage1CalculationBasis: "[Stage 1] Calculation Basis",
  stage1PercentageAmount: "[Stage 1] Percentage Amount",
  stage1AbsoluteAmount: "[Stage 1] Absolute Amount",
  stage1AbsoluteDriver: "[Stage 1] Absolute Driver",
  stage1Amount: "[Stage 1] Amount",

  // Stage 2
  stage2Timing: "[Stage 2] Timing",
  stage2EventType: "[Stage 2] Event Type",
  stage2Duration: "[Stage 2] Duration",
  stage2CalculationBasis: "[Stage 2] Calculation Basis",
  stage2PercentageAmount: "[Stage 2] Percentage Amount",
  stage2AbsoluteAmount: "[Stage 2] Absolute Amount",
  stage2AbsoluteDriver: "[Stage 2] Absolute Driver",
  stage2Amount: "[Stage 2] Amount",

  // Stage 3
  stage3Timing: "[Stage 3] Timing",
  stage3EventType: "[Stage 3] Event Type",
  stage3Duration: "[Stage 3] Duration",
  stage3CalculationBasis: "[Stage 3] Percentage Amount",
  stage3AbsoluteAmount: "[Stage 3] Absolute Amount",
  stage3AbsoluteDriver: "[Stage 3] Absolute Driver",
  stage3Amount: "[Stage 3] Amount",

  // Final
  finalTiming: "[Final] Timing",
  finalEventType: "[Final] Event Type",
  finalDuration: "[Final] Duration",
  finalCalculationBasis: "[Final] Calculation Basis",
  finalPercentageAmount: "[Final] Percentage Amount",
  finalAbsoluteAmount: "[Final] Absolute Amount",
  finalAbsoluteDriver: "[Final] Absolute Driver",
  finalAmount: "[Final] Amount",

  isTolerancePercent: "Is Tolerance Percent",
  isToleranceAbsolute: "Is Tolerance Absolute",
  tolerancePercent: "Tolerance Percent",
  toleranceAbsolute: "Tolerance Absolute",
  isBspCost: "Auto-Create Balancing ESP",
  isSharedCost: "Is Shared Cost",
  isInvoiceNeeded: "Is Invoice Needed",
  isTourEvent: "Is Tour Event",
  isOtherSupplier: "Is Other Supplier",
  isAttemptToAutoMatch: "Is Attempt to Auto Match",
  isRecalculateEsp: "Is Recalculate ESP",
  isRecalculateEspOnTermsChange: "Is Recalculate ESP on Terms Change",
  isAcceptMultipleInvoices: "Is Accept Multiple Invoices",
  isMatchToAlternativeSupplier: "Is Match to Linked Supplier",
  isMapToFinanceAccount: "Is Map to Finance Account",
  isFullMatchSourceInvoice: "Is Full Match Source Invoice",
  isSageAccountDebit: "Is Sage Account Debit",
  isSageAccountCredit: "Is Sage Account Credit",
  isUndersOversAutoMatchCode: "Is Unders/Overs Auto Match Code",
  sageAccountDebit: "Sage Account Debit",
  sageAccountCredit: "Sage Account Credit",
  undersOversAutoMatchCode: "Unders/Overs Auto Match Code",
  isAddTaxToInvoice: "Is Add Tax to Invoice",
  isTermBasedOnInvoiceDate: "Is Term Based on Invoice Date",
  isApplyAllDefaultSettings: "Is Apply All Default Settings",
  isApplyDefaultTolerance: "Is Apply Default Tolerance",
  isApplyDefaultSageCodes: "Is Apply Default Sage Codes",
  isSupersedeFinalInstallmentDueDateWithInvoiceDate:
    "Is Supersede Final Installment Due Date With Invoice Date",
  isCreateFinanceExportPostingUponMatching:
    "Is Create Finance Export Posting Upon Matching",
  isFromInvoiceUseBalance: "Is From Invoice Use Balance",
  isUseDefaultPaymentTerms: "Is Use Default Payment Terms",


  // === MAPPING ===
  mappingRemoved: "Mapping Removed",
  mappingAdded: "Mapping Added",
};

const OPERATION_OPTIONS = [
  { label: "All", value: "" },
  { label: "General", value: "General Info" },
  { label: "Payment Terms & Rules", value: "Payment Terms & Rules" },
  { label: "Mappings", value: "Mapping" },
];

const SupplierAudiTrailPage: React.FC<{ type?: "suppliers" }> = ({ type = "suppliers" }) => {
  const [logs, setLogs] = useState<IAuditLogResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [sortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [operation, setOperation] = useState<string | undefined>(undefined);

  const { id } = useParams<{ id: string }>();

  const fetchData = useCallback(async () => {
    if (!id) {
      setLogs([]);
      setInitialLoading(false);
      return;
    }
    if (!hasMore && pagination.current !== 1) return;

    try {
      if (pagination.current === 1) setInitialLoading(true);
      else setLoading(true);

      const params = {
        tableName: type ?? "suppliers",
        recordId: id,
        operation,
        isGetRawData: false,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };

      const response: IAuditLogResponse = await AuditLogServices.getAuditLogs({ params });
      const newLogs = response.data.results || [];

      setLogs((prev) => (pagination.current === 1 ? newLogs : [...prev, ...newLogs]));
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
      setHasMore(newLogs.length === pagination.pageSize);
    } catch (error) {
      console.error("Fetch audit logs error:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pagination?.current, pagination?.pageSize, sortInfo, hasMore, operation]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, id]);

  const handleLoadMore = () => {
    setPagination((prev) => ({ ...prev, current: prev.current + 1 }));
  };

  const formatValue = (field: string, value: any) => {
    if (value === null || value === undefined || value === "") return "blank";

    switch (true) {
      case /absoluteAmount$/i.test(field):
        return formatEuropeanCurrencyIntl(value) ?? "blank";
      case /percentageAmount$/i.test(field):
        return formatNumberFixed(value) ?? "blank";
      case field === "validFrom" || field === "validTo":
        return formatDateDDMMYYYY(value) ?? "blank";
      default:
        if (typeof value === "boolean") return value ? "Yes" : "No";
        return String(value);
    }
  };

  const auditData: AuditItem[] = logs.map((log) => ({
    id: log.id,
    action: log.action,
    changes: log.changes.map((c) => ({
      field: fieldLabels[c.field] || c.field,
      from: formatValue(c.field, c.oldValue),
      to: formatValue(c.field, c.newValue),
    })),
    oldData: log.oldData ?? {},
    newData: log.newData ?? {},
    updatedAt: formatDateDDMMYYYY_HHMMSS(log.updatedAt)!,
    user: log.updatedByUser || "System",
  }));



  return (
    <div className="audit-trail-page">
      <BackButton
        label={`Supplier List`}
      />
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>

        <h2 className="title-page" style={{ marginBottom: 16 }}>
          Audit Trail
        </h2>
        <Select
          style={{ width: 240 }}
          placeholder="All"
          value={operation}
          onChange={(value) => {
            setOperation(value || undefined);
            // Reset page & logs change filter
            setPagination({ current: 1, pageSize: DEFAULT_PAGINATION.pageSize, total: 0 });
            setLogs([]);
          }}
          options={OPERATION_OPTIONS}
          allowClear={false}
        />

      </Flex>
  
      <div style={{ borderBottom: "1px solid #e8e8e8", marginBottom: 30 }}></div>

      {initialLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <InfiniteAuditTrail
          items={auditData}
          loading={loading}
          hasMore={hasMore}
          initialLoading={initialLoading}
          onLoadMore={handleLoadMore}
        />
      )}
    </div>
  );
};

export default SupplierAudiTrailPage;
